import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  CheckCircle,
  XCircle,
  Loader2,
  Mail,
  ArrowRight,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { auth } from '../firebase';
import { applyActionCode, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { appPath } from '../utils/appRouting';

const AuthAction = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Parametri dall'URL di Firebase
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  const [status, setStatus] = useState('loading'); // loading, success, error
  const [error, setError] = useState('');

  useEffect(() => {
    const handleAction = async () => {
      if (!oobCode) {
        setStatus('error');
        setError('Link non valido o scaduto.');
        return;
      }

      try {
        switch (mode) {
          case 'verifyEmail':
            // Verifica email
            await applyActionCode(auth, oobCode);
            setStatus('success');
            break;

          case 'resetPassword':
            // Per reset password, verifica il codice e mostra form
            await verifyPasswordResetCode(auth, oobCode);
            // Redirect a pagina reset password con il codice
            navigate(`/reset-password?oobCode=${oobCode}`);
            break;

          case 'recoverEmail':
            // Recupero email
            await applyActionCode(auth, oobCode);
            setStatus('success');
            break;

          default:
            setStatus('error');
            setError('Azione non riconosciuta.');
        }
      } catch (err) {
        console.error('Auth action error:', err);
        setStatus('error');

        switch (err.code) {
          case 'auth/expired-action-code':
            setError('Il link è scaduto. Richiedi un nuovo link di verifica.');
            break;
          case 'auth/invalid-action-code':
            setError('Il link non è valido o è già stato utilizzato.');
            break;
          case 'auth/user-disabled':
            setError('Questo account è stato disabilitato.');
            break;
          case 'auth/user-not-found':
            setError('Utente non trovato.');
            break;
          default:
            setError('Si è verificato un errore. Riprova più tardi.');
        }
      }
    };

    handleAction();
  }, [mode, oobCode, navigate]);

  // Loading state
  if (status === 'loading') {
    return (
      <div className="h-[100dvh] overflow-y-auto bg-gradient-to-b from-brand/10 to-background flex items-center justify-center p-4">
        <div className="max-w-md w-full ds-panel p-8 text-center">
          <Loader2 className="w-16 h-16 text-brand animate-spin mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-foreground mb-4">Verifica in corso...</h2>
          <p className="text-muted-foreground">Stiamo verificando il tuo account. Attendi un momento.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="h-[100dvh] overflow-y-auto bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full ds-panel p-8 text-center">
          {/* Logo */}
          <Link to={appPath('/')} className="inline-block mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-brand to-brand-2 rounded-xl flex items-center justify-center mx-auto shadow-lg shadow-brand/20">
              <span className="text-yellow-400 font-bold text-2xl">S+</span>
            </div>
          </Link>

          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-4">Verifica non riuscita</h2>

          <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-left">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span className="text-sm">{error}</span>
          </div>

          <div className="space-y-3">
            <Button
              onClick={() => navigate(appPath('/login'))}
              className="w-full ds-btn-gradient"
            >
              Vai al Login
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate(appPath('/register'))}
              className="w-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Registrati di nuovo
            </Button>
          </div>

          <p className="mt-6 text-sm text-muted-foreground">
            Hai bisogno di aiuto?{' '}
            <a href="mailto:support@sostegnopro.it" className="text-brand hover:underline">
              Contattaci
            </a>
          </p>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="h-[100dvh] overflow-y-auto bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full ds-panel p-8 text-center">
        {/* Logo */}
        <Link to={appPath('/')} className="inline-block mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-brand to-brand-2 rounded-xl flex items-center justify-center mx-auto shadow-lg shadow-brand/20">
            <span className="text-yellow-400 font-bold text-2xl">S+</span>
          </div>
        </Link>

        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>

        <h2 className="text-2xl font-bold text-foreground mb-2">Email verificata!</h2>

        <p className="text-muted-foreground mb-8">
          Il tuo account è stato verificato con successo. Ora puoi accedere a tutte le funzionalità di SOSTEGNO PRO+.
        </p>

        {/* Features unlocked */}
        <div className="bg-muted/40 rounded-xl p-4 mb-6 text-left border border-border">
          <p className="font-medium text-foreground mb-2">Cosa puoi fare ora:</p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Gestire i tuoi alunni
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Creare PEI e documenti
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Usare l'AI Didattica
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Esportare in PDF
            </li>
          </ul>
        </div>

        <Button
          onClick={() => navigate(appPath('/login'))}
          className="w-full ds-btn-gradient h-12"
        >
          Accedi al tuo account
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>

        <p className="mt-6 text-sm text-muted-foreground">Benvenuto in SOSTEGNO PRO+! 🎉</p>
      </div>
    </div>
  );
};

export default AuthAction;
